import { type ReactNode, useEffect, useRef } from 'react';

import { List } from '@material-ui/core';

import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';

import { useAutocompleteContext } from './SimpleAutocomplete';
import styles from './SimpleAutocompleteVirtualizedList.module.css';

const DEFAULT_ITEM_SIZE = 36;
const DEFAULT_OVERSCAN = 5;

type VirtualizedListItem = { value: string; label?: string };

type VirtualizedListProps = {
  renderItem: (item: VirtualizedListItem, index: number) => ReactNode;
  className?: string;
  itemSize?: number;
  overscan?: number;
  // Called when the last item has been rendered; use for load-more.
  onEndReached?: () => void;
};

export const VirtualizedList = ({
  renderItem,
  className,
  itemSize = DEFAULT_ITEM_SIZE,
  overscan = DEFAULT_OVERSCAN,
  onEndReached,
}: VirtualizedListProps) => {
  const { items, getListboxProps, highlightedIndex } = useAutocompleteContext();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => itemSize,
    overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (highlightedIndex >= 0) {
      virtualizer.scrollToIndex(highlightedIndex, { align: 'auto' });
    }
  }, [highlightedIndex, virtualizer]);

  const lastRenderedIndex = virtualItems.at(-1)?.index;

  useEffect(() => {
    if (
      lastRenderedIndex !== undefined &&
      lastRenderedIndex >= items.length - 1
    ) {
      onEndReached?.();
    }
  }, [lastRenderedIndex, items.length, onEndReached]);

  if (items.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      className={classNames(styles.scrollContainer, className)}
    >
      <List
        disablePadding
        className={styles.spacer}
        style={{ height: virtualizer.getTotalSize() }}
        {...getListboxProps()}
      >
        {virtualItems.map(virtualItem => (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className={styles.row}
            style={{ transform: `translateY(${virtualItem.start}px)` }}
          >
            {renderItem(items[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </List>
    </div>
  );
};
